import { motion } from 'framer-motion';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { MessageSquare, Search, Loader2 } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

const formatTime = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m`;
  if (diffHours < 24) return `${diffHours}h`;
  if (diffDays < 7) return `${diffDays}d`;
  return date.toLocaleDateString();
};

const getRoleBadge = (otherUser, t) => {
  if (otherUser?.user_subcategory === 'pastor') return { text: t('pastor'), color: 'bg-purple-500' };
  if (otherUser?.role === 'teacher') return { text: t('teacher'), color: 'bg-blue-500' };
  if (otherUser?.role === 'admin') return { text: t('admin'), color: 'bg-amber-500' };
  return null;
};

export const ConversationList = ({
  conversations,
  loading,
  searchQuery,
  setSearchQuery,
  messageSearchQuery,
  setMessageSearchQuery,
  isSearching,
  showSearchResults,
  messageSearchResults,
  onSelectConversation,
  onGoToSearchResult,
  onClearSearch,
  activeConversationId
}) => {
  const { t } = useLanguage();

  const filteredConversations = conversations.filter(conv => 
    conv.other_user?.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="w-80 bg-slate-900 border-r border-slate-800 flex flex-col flex-shrink-0">
      {/* Search - Conversations */}
      <div className="p-3 border-b border-slate-800 space-y-2">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder={t('search_conversations')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 bg-slate-800 border-slate-700 text-sm"
            data-testid="conversation-filter"
          />
        </div>
        
        {/* Message Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-pink-400" />
          <Input
            placeholder={t('search_messages')}
            value={messageSearchQuery}
            onChange={(e) => setMessageSearchQuery(e.target.value)}
            className="pl-9 bg-slate-800 border-slate-700 text-sm border-pink-500/30 focus:border-pink-500"
            data-testid="message-search"
          />
          {isSearching && (
            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-pink-400" />
          )}
          {messageSearchQuery && !isSearching && (
            <button
              onClick={onClearSearch}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Search Results or Conversations */}
      <ScrollArea className="flex-1">
        {showSearchResults ? (
          // Message Search Results
          <div className="p-2">
            <div className="flex items-center justify-between px-2 py-1 mb-2">
              <span className="text-xs text-slate-400">
                {messageSearchResults.length} {messageSearchResults.length !== 1 ? 'results' : 'result'} for &quot;{messageSearchQuery}&quot;
              </span>
              <button
                onClick={onClearSearch}
                className="text-xs text-pink-400 hover:text-pink-300"
              >
                {t('close')}
              </button>
            </div>
            {messageSearchResults.length === 0 ? (
              <div className="text-center py-8">
                <Search className="w-10 h-10 mx-auto mb-2 text-slate-600" />
                <p className="text-sm text-slate-400">{t('no_results')}</p>
              </div>
            ) : (
              <div className="space-y-2">
                {messageSearchResults.map((result, idx) => (
                  <motion.div
                    key={result.message_id || idx}
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.03 }}
                    className="p-3 rounded-lg bg-slate-800/50 hover:bg-slate-800 cursor-pointer transition-colors"
                    onClick={() => onGoToSearchResult(result)}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Avatar className="w-6 h-6">
                        <AvatarImage src={result.conversation?.other_user?.picture} />
                        <AvatarFallback className="text-xs">
                          {result.conversation?.other_user?.name?.charAt(0) || '?'}
                        </AvatarFallback>
                      </Avatar>
                      <span className="text-xs font-medium text-slate-300">
                        {result.sender_name}
                      </span>
                      <span className="text-xs text-slate-500">
                        {formatTime(result.created_at)}
                      </span>
                    </div>
                    <p className="text-sm text-slate-300 line-clamp-2">
                      {result.content}
                    </p>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        ) : loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : filteredConversations.length === 0 ? (
          <div className="text-center py-12 px-4">
            <MessageSquare className="w-12 h-12 mx-auto mb-3 text-slate-600" />
            <p className="text-slate-400">{t('no_conversations')}</p>
            <p className="text-xs text-slate-500 mt-1">{t('start_conversation')}</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-800">
            {filteredConversations.map((conv) => {
              const badge = getRoleBadge(conv.other_user, t);
              const isActive = activeConversationId === conv.conversation_id;
              
              return (
                <motion.div
                  key={conv.conversation_id}
                  whileHover={{ backgroundColor: 'rgba(255,255,255,0.05)' }}
                  className={`p-3 cursor-pointer transition-colors ${
                    isActive ? 'bg-slate-800' : ''
                  }`}
                  onClick={() => onSelectConversation(conv)}
                >
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <Avatar className="w-12 h-12">
                        <AvatarImage src={conv.other_user?.picture} />
                        <AvatarFallback>{conv.other_user?.name?.charAt(0) || '?'}</AvatarFallback>
                      </Avatar>
                      {conv.unread > 0 && (
                        <span className="absolute -top-1 -right-1 w-5 h-5 bg-pink-500 rounded-full text-xs flex items-center justify-center font-bold">
                          {conv.unread}
                        </span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium truncate">{conv.other_user?.name}</span>
                        {badge && (
                          <Badge className={`${badge.color} text-white text-xs py-0`}>
                            {badge.text}
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-slate-400 truncate">
                        {conv.last_message || t('no_conversations')}
                      </p>
                    </div>
                    <span className="text-xs text-slate-500">
                      {conv.last_message_at && formatTime(conv.last_message_at)}
                    </span>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </ScrollArea>
    </div>
  );
};

export default ConversationList;
